
import React, { useState, useEffect, useRef, ChangeEvent } from "react";
import { Button } from "../../../Components/ui/button";
import Default_WhatsApp_background from "../../../Assets/Default_WhatsApp_background.jpeg";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../Components/ui/select";
import { Input } from "../../../Components/ui/input";
import { Label } from "../../../Components/ui/label";
import { FaTrash, FaFilePdf } from "react-icons/fa";
import { PlusIcon, FontBoldIcon, FontItalicIcon, CodeIcon } from "@radix-ui/react-icons";
import { Smile, Strikethrough, AlertTriangle, Info } from "lucide-react";
import EmojiPicker from "emoji-picker-react";
import { Card } from "../../../Components/ui/card";
import { Textarea } from "../../../Components/ui/textarea";
import emojiRegex from 'emoji-regex';
import { useToast } from "../../../Components/ui/use-toast";
import { Toaster } from "../../../Components/ui/toaster";
import { Checkbox } from "../../../Components/ui/checkbox"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from "../../../Components/ui/dialog";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '../../../Components/ui/hover-card';
import { getSmsPartsCount } from "../../../Pages/Campaign/CreateCampaign/utils/getSmsPartsCount";
import axios from "axios";
import { useSelector } from "react-redux";
import { RootState } from "@/src/State/store";


// ============================================================================
// INTERFACES & TYPES
// ============================================================================

interface CountryCode {
  label: string;
  value: string;
}

interface LanguageOption {
  label: string;
  value: string;
}

interface RowData {
  buttonType: string;
  buttonText: string;
  buttonTypeDropdown: string;
  websiteUrl: string;
  countryCode: string;
  callPhoneNumber: string;
  copyOfferCode: string;
  quickReplyResponse?: string;
  optOut?: boolean;
  optOutType?: "trip" | "permanent";
}

interface ValidationErrors {
  language: string;
  headerText: string;
  bodyText: string;
  footerText: string;
  mediaFile: string;
  headerContent: string;
  templateName: string;
}

interface ButtonValidationErrors {
  buttonText: string;
  websiteUrl: string;
  phoneNumber: string;
  countryCode: string;
  copyOfferCode: string;
  general: string;
}

interface CreateContentFormProps {
  onClose: () => void;
  onSave: (content: any) => void;
  initialContent?: any;
  allTemplates?: any[];
  mode?: 'create' | 'edit' | 'view';
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

// Extract SMS message from various possible data structures
function extractSMSMessage(content: any): string {
  if (!content) return "";

  const paths = [
    { value: content.message, condition: typeof content.message === "string" && content.message.trim() },
    { value: content.template?.message, condition: typeof content.template?.message === "string" && content.template?.message.trim() },
    { value: content.template_content?.message, condition: typeof content.template_content?.message === "string" && content.template_content?.message.trim() },
    { value: content._translatedTemplate?.template?.message, condition: !!content._translatedTemplate?.template?.message },
    { value: content.translated_template?.template?.message, condition: !!content.translated_template?.template?.message },
  ];

  if (content.channels && Array.isArray(content.channels)) {
    for (const channel of content.channels) {
      if (channel.channel_id === 2 && channel.translated_template?.template?.message) {
        return channel.translated_template.template.message;
      }
    }
  }

  if (content.channel_id === 2 && content.translated_template?.template?.message) {
    return content.translated_template.template.message;
  }

  for (const path of paths) {
    if (path.condition && path.value) {
      return path.value;
    }
  }

  return "";
}

// Extract media config from WhatsApp template
function extractMediaConfig(initialContent: any) {
  let media_config =
    initialContent?.media_config ||
    initialContent?.translated_template?.media_config ||
    initialContent?.template?.media_config;

  if (
    (!media_config || !media_config.binary_data) &&
    Array.isArray(initialContent?.components)
  ) {
    const headerComp = initialContent.components.find((c: any) => c.type === "HEADER");
    if (headerComp?.media_config?.binary_data) {
      media_config = headerComp.media_config;
    }
  }
  if (
    (!media_config || !media_config.binary_data) &&
    Array.isArray(initialContent?.template?.components)
  ) {
    const headerComp = initialContent.template.components.find((c: any) => c.type === "HEADER");
    if (headerComp?.media_config?.binary_data) {
      media_config = headerComp.media_config;
    }
  }
  return media_config;
}

// Validation helper functions
const containsEmoji = (text: string): boolean => {
  const regex = emojiRegex();
  return regex.test(text);
};

const normalizeUrl = (raw: string) => {
  const v = (raw || "").trim();
  if (!v) return "";
  if (/^https:\/\//i.test(v)) return v;
  if (/^www\./i.test(v)) return `https://${v}`;
  return `https://${v.replace(/^http:\/\//i, "")}`;
};

const containsFormatting = (text: string): boolean => {
  const formattingRegex = /[\*_~`]/;
  return formattingRegex.test(text);
};

const validateLanguage = (language: string): string => {
  if (!language || language.trim() === '') {
    return 'Language selection is required';
  }
  return '';
};

const validateHeaderText = (text: string): string => {
  if (!text || text.trim() === '') return '';
  if (containsEmoji(text)) {
    return 'Header text cannot contain emojis';
  }
  if (containsFormatting(text)) {
    return 'Header text cannot contain formatting characters (*, _, ~, `)';
  }
  return '';
};

// const validateBodyText = (text: string, channelId: number): string => {
//   if (!text || text.trim() === '') {
//     return 'Body text is required';
//   }

//   // Check variable at start or end
//   const startsWithVar = /^\s*\{\{\d+\}\}/.test(text);
//   const endsWithVar = /\{\{\d+\}\}\s*$/.test(text);
//   if (startsWithVar || endsWithVar) {
//     return 'Variables cannot be placed at the start or end of the body text';
//   }
//   // WhatsApp requires minimum 25 characters
//   if (channelId === 1 && text.trim().length < 25) {
//     return 'Body text must be at least 25 characters long';
//   }
//   return '';
// };
const validateBodyText = (text: string, channelId: number): string => {
  if (!text || text.trim() === '') {
    return 'Body text is required';
  }

  const startsWithVar = /^\s*\{\{\d+\}\}/.test(text);
  const endsWithVar = /\{\{\d+\}\}\s*$/.test(text);

  if (channelId === 1) {
    // WhatsApp: block both start and end
    if (startsWithVar || endsWithVar) {
      return 'Variables cannot be placed at the start or end of the body text';
    }
    if (text.trim().length < 25) {
      return 'Body text must be at least 25 characters long';
    }
  } else if (channelId === 2) {
    // SMS: only block start
    if (startsWithVar) {
      return 'Variables cannot be placed at the start of the body text';
    }
  }

  return '';
};



const validateFooterText = (text: string): string => {
  if (!text || text.trim() === '') return '';
  if (containsEmoji(text)) {
    return 'Footer text cannot contain emojis';
  }
  if (containsFormatting(text)) {
    return 'Footer text cannot contain formatting characters (*, _, ~, `)';
  }
  return '';
};

const validateMediaFile = (file: File | null): string => {
  if (!file) return '';
  const maxSize = 5 * 1024 * 1024; // 5MB
  if (file.size > maxSize) {
    return 'File size must not exceed 5MB';
  }
  return '';
};



const validateHeaderContent = (headerType: string, textInput: string, hasMedia: boolean): string => {
  if (headerType === 'none') return '';
  if (headerType === 'text') {
    if (!textInput || textInput.trim() === '') {
      return 'Header text is required when header type is selected';
    }
  } else if (['image', 'video', 'document'].includes(headerType)) {
    if (!hasMedia) {
      return `${headerType.charAt(0).toUpperCase() + headerType.slice(1)} is required when header type is selected`;
    }
  }
  return '';
};

const validateButtonText = (text: string, buttonType: string): string => {
  if (buttonType === 'COPY_CODE') return '';
  const v = (text ?? '').trim();
  if (!v) return 'Button text is required';
  if (v.length > 25) return 'Button text cannot exceed 25 characters';
  if (/(https?:\/\/|www\.)/i.test(v)) return 'Do not put URLs in button text';
  if (!/^[A-Za-z0-9 ]+$/.test(v)) return 'Button text may contain only letters, numbers, and spaces';
  return '';
};

const validateWebsiteUrl = (url: string): string => {
  if (!url || url.trim() === '') {
    return 'Website URL is required';
  }
  const httpsRegex = /^https:\/\//;
  const wwwRegex = /^www\./;
  if (!httpsRegex.test(url) && !wwwRegex.test(url)) {
    return 'URL must start with https:// or www.';
  }
  return '';
};

const validatePhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber || phoneNumber.trim() === '') {
    return 'Phone number is required';
  }
  const numbersOnlyRegex = /^\d+$/;
  if (!numbersOnlyRegex.test(phoneNumber)) {
    return 'Phone number can only contain numbers';
  }
  if (phoneNumber.length < 7 || phoneNumber.length > 15) {
    return 'Phone number must be between 7 and 15 digits';
  }
  return '';
};

const validateCountryCode = (code: string, buttonType: string): string => {
  if (buttonType === "PHONE_NUMBER") {
    if (!code || code.trim() === "") return "Country code is required";
  }
  return "";
};

const validateCopyOfferCode = (code: string): string => {
  const v = (code ?? '').trim();
  if (!v) return 'Offer code is required';
  if (v.length > 15) return 'Offer code cannot exceed 15 characters';
  if (!/^[A-Za-z0-9_-]+$/.test(v)) {
    return 'Only letters, numbers, dashes (-), and underscores (_) are allowed';
  }
  if (containsEmoji(v)) return 'Offer code cannot contain emojis';
  if (/(https?:\/\/|www\.)/i.test(v)) return 'Offer code cannot contain URLs';
  return '';
};

const validateTemplateName = (name: string): string => {
  if (!name || name.trim() === '') {
    return 'Template name is required';
  }
  const regex = /^[a-z][a-z0-9_]*$/;
  if (!regex.test(name)) {
    return 'Template name must start with a lowercase letter and contain only lowercase letters, numbers, and underscore (_)';
  }
  return '';
};

// Text area icon for empty preview
const textAreaIcon = () => (
  <div className="relative w-8 h-8 flex justify-center items-center">
    <div
      className="absolute rounded w-[3px] h-8 transform rotate-45"
      style={{ borderLeft: "3px solid #64748B" }}
    />
    <div
      className="absolute w-[3px] h-4 rounded transform rotate-45 translate-x-2 translate-y-2"
      style={{ borderLeft: "3px solid #64748B" }}
    />
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const CreateContentForm: React.FC<CreateContentFormProps> = ({
  onClose,
  onSave,
  initialContent,
  allTemplates = [],
  mode = 'create'
}) => {
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================

  // Common states
  const [partnerId, setPartnerId] = useState<string>("");
  const [channelConfigId, setChannelConfigId] = useState<string>("");
  const [selectedChannelId, setSelectedChannelId] = useState<number | null>(null);
  const [templateName, setTemplateName] = useState("");
  const [partners, setPartners] = useState<any[]>([]);
  const [configs, setConfigs] = useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);

  // SMS specific states
  const [smsBody, setSmsBody] = useState<string>(extractSMSMessage(initialContent));
  const [partsCount, setPartsCount] = useState(1);
  const [showSmsWarning, setShowSmsWarning] = useState(false);
  const [smsBodyError, setSmsBodyError] = useState<string>("");

  // WhatsApp specific states
  const [language, setLanguage] = useState("");
  const [headerType, setHeaderType] = useState<string>("none");
  const [textInput, setTextInput] = useState<string>("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [bodyText, setBodyText] = useState<string>("");
  const [footerText, setFooterText] = useState<string>("");
  const [rows, setRows] = useState<RowData[]>([]);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [mediaBase64, setMediaBase64] = useState("");
  const [showPicker, setShowPicker] = useState(false);
  const [fileName, setFileName] = useState<string>("");
  const [fileType, setFileType] = useState<string>("");
  const [fileLength, setFileLength] = useState<string>("");



  const [countryCodes, setCountryCodes] = useState<CountryCode[]>([]);
  const [countryCodesLoading, setCountryCodesLoading] = useState(false);
  const [hasFetchedCountryCodes, setHasFetchedCountryCodes] = useState(false);

  const [languages, setLanguages] = useState<LanguageOption[]>([]);
  const [languagesLoading, setLanguagesLoading] = useState(false);
  const [hasFetchedLanguages, setHasFetchedLanguages] = useState(false);
  const [templateNameValid, setTemplateNameValid] = useState(true);
  const [operatorSearchQuery, setOperatorSearchQuery] = useState("");
  const [configSearchQuery, setConfigSearchQuery] = useState("");
  const [variables, setVariables] = useState<{ name: string; value: string }[]>([]);
  const [smsVariables, setSmsVariables] = useState<{ name: string; value: string }[]>([]);
  const VARIABLE_OPTIONS_WHATSAPP = ["From_Country", "To_Country"];
  const VARIABLE_OPTIONS_SMS = ["From_Country", "To_Country", "Optout-Reference"];
  // ✅ FIX
  const [nextVarIndex, setNextVarIndex] = useState(1);
  const [nextSmsVarIndex, setNextSmsVarIndex] = useState(1);
  const [missingVariablesWarning, setMissingVariablesWarning] = useState<string>("");

  const toast = useToast();
  const apiUrlAdvAcc = useSelector((state: RootState) => state.authentication.apiURL);
  const [uploadError, setUploadError] = useState<string | null>(null);


  // Validation states
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({
    language: '',
    headerText: '',
    bodyText: '',
    footerText: '',
    mediaFile: '',
    headerContent: '',
    templateName: ''
  });
  const [buttonValidationErrors, setButtonValidationErrors] = useState<ButtonValidationErrors[]>([]);

  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Redux selectors
  const accountId = useSelector((state: RootState) => state.authentication.account_id);
  const adminUrl = useSelector((state: RootState) => state.authentication.adminUrl);
  const operatorUrl = useSelector(
    (state: RootState) => state.authentication.operatorUrl,
  );
  const workspaceId = useSelector((state: RootState) => state.authentication.workspace_id);
  const advUrl = useSelector((state: RootState) => state.authentication.apiURL);
  const workspaceName = useSelector(
    (state: RootState) => state.authentication.workspaceName
  );

  // ============================================================================
  // EFFECTS
  // ============================================================================

  useEffect(() => {
    loadPartners();
  }, []);

  // Fetch languages and country codes on mount for WhatsApp
  useEffect(() => {
    if (!hasFetchedLanguages && !languagesLoading) {
      fetchLanguages();
    }
  }, []);

  useEffect(() => {
    if (!hasFetchedCountryCodes && !countryCodesLoading) {
      fetchCountryCodes();
    }
  }, []);

  // SMS body character count and parts calculation
  // WhatsApp — show warning immediately, keep row
  useEffect(() => {
    if (variables.length === 0) { setMissingVariablesWarning(""); return; }
    const missing = variables.filter((v) => !bodyText.includes(v.name));
    if (missing.length > 0) {
      setMissingVariablesWarning(`${missing.map(v => v.name).join(", ")} variable is missing in the body of content`);
    } else {
      setMissingVariablesWarning("");
    }
  }, [bodyText, variables]);

  useEffect(() => {
    if (smsVariables.length === 0) { setMissingVariablesWarning(""); return; }

    // Priority 1: check if any pill's tag is missing from the body
    const missing = smsVariables.filter((v) => !smsBody.includes(v.name.trim()));
    if (missing.length > 0) {
      setMissingVariablesWarning(`${missing.map(v => v.name).join(", ")} variable is missing in the body of content`);
      return;
    }

    // Priority 2: check sequence (only when all tags are present in body)
    const numbers = Array.from(smsBody.matchAll(/\{\{(\d+)\}\}/g)).map((m) => Number(m[1]));
    const uniqueNumbers = Array.from(new Set(numbers)).sort((a, b) => a - b);
    const isSequential = uniqueNumbers.every((n, i) => n === i + 1);

    if (uniqueNumbers.length > 0 && !isSequential) {
      const expected = uniqueNumbers.map((_, i) => `{{${i + 1}}}`).join(", ");
      setMissingVariablesWarning(`Variables must be in sequence. Expected: ${expected}`);
      return;
    }

    setMissingVariablesWarning("");
  }, [smsBody, smsVariables]);

  // ✅ Guard it so it doesn't override view mode's channel
  useEffect(() => {
    if (mode !== 'view' && !selectedChannelId && !channelConfigId) {
      setSelectedChannelId(1);
    }
  }, [selectedChannelId, channelConfigId, mode]);




  useEffect(() => {
    if ((mode === 'view' || mode === 'edit') && initialContent) {

      // Set locked fields
      setPartnerId(initialContent.partner_id?.toString() || '');
      setChannelConfigId(initialContent.target_id?.toString() || '');  // fix: was channel_config_id
      setTemplateName(initialContent.template_name || '');
      setSelectedChannelId(initialContent.channel_type);               // fix: was missing

      // Parse — template_content is double-nested
      const outerContent = JSON.parse(initialContent.template_content || '{}');
      const content = outerContent.template || {};

      if (initialContent.channel_type === 1) {
        // WhatsApp
        setLanguage(content.language || '');

        if (Array.isArray(content.components)) {
          content.components.forEach((comp: any) => {
            if (comp.type === 'HEADER') {
              if (comp.format === 'TEXT') {
                setHeaderType('text');
                setTextInput(comp.text || '');
              } else if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(comp.format)) {
                setHeaderType(comp.format.toLowerCase());
                const mc = outerContent.media_config;
                if (mc) {
                  setMediaBase64(mc.binary_data || '');
                  setFileName(mc.file_name || '');
                  setFileType(mc.file_type || '');
                }
              }
              // } else if (comp.type === 'BODY') {
              //   setBodyText(comp.text || '');
            } else if (comp.type === 'BODY') {
              setBodyText(comp.text || '');

              // Populate variables from example.body_text
              // example: { body_text: [["India", "UAE"]] }
              const exampleValues: string[] = comp.example?.body_text?.[0] || [];

              if (exampleValues.length > 0) {
                // Reverse map: "India" -> "From_Country", "UAE" -> "To_Country"
                // const reverseValueMap: Record<string, string> = {
                //   "India": "From_Country",
                //   "UAE": "To_Country",
                // };

                // Extract {{1}}, {{2}} etc from body text in order
                const tagMatches = [...(comp.text || '').matchAll(/\{\{(\d+)\}\}/g)];
                // ✅ FIX
                const uniqueTags = Array.from(new Set(tagMatches.map((m) => m[0])));

                const populatedVariables = uniqueTags.map((tag, i) => ({
                  name: tag,
                  value: exampleValues[i] || '',  // already "From_Country", "To_Country"
                }));

                setVariables(populatedVariables);
              }
            } else if (comp.type === 'FOOTER') {
              setFooterText(comp.text || '');
              // ✅ FIX — match each QUICK_REPLY button with its QR entry by label
            } else if (comp.type === 'BUTTONS') {
              // QR data is in template_content.QR (parsed from outerContent)
              const qrList: any[] = outerContent.QR || [];

              const mappedButtons = comp.buttons?.map((btn: any) => {
                const uiButtonType = btn.type === 'QUICK_REPLY' ? 'CUSTOM' : btn.type;

                // ✅ Find matching QR entry by label to restore response and optOut
                const qrMatch = qrList.find((qr: any) => qr.label === btn.text);

                return {
                  buttonType: uiButtonType,
                  buttonText: btn.text || '',
                  buttonTypeDropdown: uiButtonType,
                  websiteUrl: btn.url || '',
                  countryCode: (() => {
                    const phone = btn.phone_number || '';
                    // Try space-separated first: "91 6381354818"
                    if (phone.includes(' ')) return phone.split(' ')[0];
                    // Try matching known country code lengths (1-3 digits) against countryCodes list
                    // Fallback: treat first 2 chars as country code
                    return '';
                  })(),
                  callPhoneNumber: (() => {
                    const phone = btn.phone_number || '';
                    if (phone.includes(' ')) return phone.split(' ')[1] || '';
                    // No space — put entire number in phone field, let user fix country code
                    return phone;
                  })(),
                  copyOfferCode: btn.example?.[0] || '',
                  quickReplyResponse: qrMatch?.response || '',   // ✅ from QR array
                  optOut: (qrMatch?.opt_out ?? 0) > 0,
                  optOutType: qrMatch?.opt_out === 2 ? "permanent" : "trip",            // ✅ from QR array
                };
              }) || [];
              setRows(mappedButtons);
            }
          });
        }

      } else if (initialContent.channel_type === 2) {
        // SMS
        const message =
          content.message ||
          outerContent.template_content?.message ||
          '';
        setSmsBody(message);

        // Extract {{1}}, {{2}} etc from SMS body and populate smsVariables
        const tagMatches = [...message.matchAll(/\{\{(\d+)\}\}/g)];
        const uniqueTags = Array.from(new Set(tagMatches.map((m: any) => m[0])));

        if (uniqueTags.length > 0) {
          const exampleValues: string[] =
            content.example?.body_text?.[0] ||
            outerContent.template_content?.example?.body_text?.[0] ||
            [];

          const populatedVariables = uniqueTags.map((tag, i) => ({
            name: tag,
            value: exampleValues[i] || '',  // already "From_Country", "To_Country", "Optout-Reference"
          }));

          setSmsVariables(populatedVariables);
        }
      }
    }
  }, [mode, initialContent]);
  // ============================================================================
  // API FUNCTIONS
  // ============================================================================

  const loadPartners = async () => {
    try {
      const res = await axios.get(`${adminUrl}/GetActiveOperator`);
      setPartners(Array.isArray(res.data.data) ? res.data.data : []);
    } catch (err) {
      console.error("Failed to load partners", err);
      setPartners([]);
    }
  };

  const handlePartnerChange = async (value: string) => {
    setPartnerId(value);
    setChannelConfigId("");
    setConfigSearchQuery('');


    try {
      const res = await axios.get(`${adminUrl}/GetActiveChannelsOfOperator`, {
        params: { partnerId: value },
      });

      if (res.data.status === "Success") {
        setConfigs(res.data.data);
      } else {
        setConfigs([]);
      }
    } catch (err) {
      console.error("Failed to load active channels", err);
      setConfigs([]);
    }
  };

  const fetchLanguages = async () => {
    setLanguagesLoading(true);
    try {
      const response = await axios.get(`${advUrl}/GetLanguages`);
      if (response.data.status === "Success") {
        setLanguages(response.data.languages);
        setHasFetchedLanguages(true);
      } else {
        setLanguages([]);
        toast.toast({
          title: "Error",
          description: "Unable to fetch languages.",
        });
      }
    } catch (error) {
      setLanguages([]);
      toast.toast({
        title: "Error",
        description: "Error fetching languages.",
      });
    } finally {
      setLanguagesLoading(false);
    }
  };

  const fetchCountryCodes = async () => {
    setCountryCodesLoading(true);
    try {
      const response = await axios.get(`${advUrl}/GetAllCountryCodes`);
      if (response.data.status === "Success") {
        setCountryCodes(response.data.country_codes);
        setHasFetchedCountryCodes(true);
      } else {
        setCountryCodes([]);
        toast.toast({
          title: "Error",
          description: "Unable to fetch country codes.",
        });
      }
    } catch (error: any) {
      setCountryCodes([]);
      toast.toast({
        title: "Error",
        description: "Error fetching country codes.",
      });
    } finally {
      setCountryCodesLoading(false);
    }
  };

  // ============================================================================
  // HANDLER FUNCTIONS
  // ============================================================================

  const handleConfigChange = (value: string) => {
    setChannelConfigId(value);
    const selectedConfig = configs.find(c => c.channelConfigId.toString() === value);
    const channelId = selectedConfig?.channelId || null;
    setSelectedChannelId(channelId);

    // Reset form data when switching channels
    if (channelId === 2) {
      // Switching to SMS
      setSmsBody("");
      setPartsCount(1);
      setShowSmsWarning(false);
      setTemplateName("");
    } else if (channelId === 1) {
      // Switching to WhatsApp
      setLanguage("");
      setHeaderType("none");
      setTextInput("");
      setBodyText("");
      setFooterText("");
      setRows([]);
      setSelectedFile(null);
      setMediaBase64("");
      setTemplateName("");
    }

    // Reset validation errors
    setValidationErrors({
      language: '',
      headerText: '',
      bodyText: '',
      footerText: '',
      mediaFile: '',
      headerContent: '',
      templateName: ''
    });
    setButtonValidationErrors([]);
  };

  // SMS handlers
  const formatSmsText = (text: string) => {
    return text
      .replace(/\*(.*?)\*/g, "<strong>$1</strong>")
      .replace(/_(.*?)_/g, "<em>$1</em>");
  };

  // WhatsApp handlers
  const handleLanguageChange = (value: string) => {
    setLanguage(value);
    setValidationErrors(prev => ({ ...prev, language: validateLanguage(value) }));
  };

  const handleHeaderTypeChange = (value: string) => {
    setHeaderType(value);
    setTextInput("");
    setSelectedFile(null);
    setPreviewImage(null);
    setMediaBase64("");
    setFileName("");
    setFileType("");
    setFileLength("");
    setValidationErrors(prev => ({
      ...prev,
      headerText: '',
      mediaFile: '',
      headerContent: ''
    }));
  };

  const handleHeaderTextChange = (value: string) => {
    if (value.length <= 60) {
      setTextInput(value);
      setValidationErrors(prev => ({ ...prev, headerText: validateHeaderText(value) }));
    }
  };


  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const ext = file.name.split(".").pop()?.toLowerCase() || "";
    const allowedImageExts = ["jpeg", "jpg", "png"];
    const allowedVideoExts = ["mp4"];
    const allowedDocExts = ["pdf"];

    const isImage = file.type.startsWith("image/") && allowedImageExts.includes(ext);
    const isVideo = file.type === "video/mp4" && allowedVideoExts.includes(ext);
    const isPdf = file.type === "application/pdf" && allowedDocExts.includes(ext);

    if (!isImage && !isVideo && !isPdf) {
      setUploadError("Only JPG, JPEG, PNG images, MP4 videos, or PDF documents are allowed.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setUploadError(null); // ✅ clear error if valid

    const mediaFileError = validateMediaFile(file);

    if (mediaFileError) {
      setValidationErrors(prev => ({
        ...prev,
        mediaFile: mediaFileError,
        headerContent: validateHeaderContent(headerType, textInput, false)
      }));
      setSelectedFile(null);
      setFileName('');
      setFileType('');
      setFileLength('');
      setPreviewImage(null);
      setMediaBase64('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      toast.toast({
        title: "File Error",
        description: mediaFileError,
      });
      return;
    }

    // ✅ Set file info FIRST
    setSelectedFile(file);
    setFileName(file.name);
    setFileType(file.type);
    setFileLength(file.size.toString());
    setValidationErrors(prev => ({ ...prev, mediaFile: '' }));

    // ✅ Handle preview for ALL media types
    if (headerType === "image") {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
    // ✅ For video and document, we don't need previewImage
    // The selectedFile itself will be used to create preview

    // ✅ Convert to base64 for backend
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = (reader.result as string).split(",")[1];
      setMediaBase64(base64String);
      setValidationErrors(prev => ({
        ...prev,
        headerContent: validateHeaderContent(headerType, textInput, true)
      }));
    };
    reader.readAsDataURL(file);
  };
  const renderFormattedText = (text: string) => {
    return text
      .replace(/\*(.*?)\*/g, "<strong>$1</strong>")
      .replace(/_(.*?)_/g, "<em>$1</em>")
      .replace(/~(.*?)~/g, "<s>$1</s>")
      .replace(/```(.*?)```/g, "<code>$1</code>");
  };

  const handleBodyTextChange = (value: string) => {
    setBodyText(value);
    setMissingVariablesWarning("");  // ← ADD
    setValidationErrors(prev => ({
      ...prev,
      bodyText: validateBodyText(value, selectedChannelId || 1)
    }));
  };

  const handleFooterTextChange = (value: string) => {
    setFooterText(value);
    setValidationErrors(prev => ({ ...prev, footerText: validateFooterText(value) }));
  };

  const addButtonRow = () => {
    if (rows.length >= 3) return;

    setRows([
      ...rows,
      {
        buttonType: "URL",
        buttonText: "",
        buttonTypeDropdown: "URL",
        websiteUrl: "",
        countryCode: "",
        callPhoneNumber: "",
        copyOfferCode: "",
        quickReplyResponse: "",
        optOut: false,
        optOutType: "trip",
      },
    ]);
    setButtonValidationErrors([
      ...buttonValidationErrors,
      {
        buttonText: '',
        websiteUrl: '',
        phoneNumber: '',
        countryCode: '',
        copyOfferCode: '',
        general: ''
      }
    ]);
  };

  const removeButtonRow = (index: number) => {
    setRows(rows.filter((_, i) => i !== index));
    setButtonValidationErrors(buttonValidationErrors.filter((_, i) => i !== index));
  };

  const updateButtonRow = (index: number, field: keyof RowData, value: string) => {
    const newRows = [...rows];
    newRows[index] = { ...newRows[index], [field]: value };
    setRows(newRows);

    // Validate the specific field
    const newErrors = [...buttonValidationErrors];
    if (!newErrors[index]) {
      newErrors[index] = {
        buttonText: '',
        websiteUrl: '',
        phoneNumber: '',
        countryCode: '',
        copyOfferCode: '',
        general: ''
      };
    }

    const row = newRows[index];

    if (field === 'buttonText') {
      // ✅ Rule 1: Check for duplicate CUSTOM (Quick Reply) button text
      if (row.buttonType === 'CUSTOM') {
        const currentText = (value ?? '').trim().toLowerCase();
        const isDuplicate = newRows.some(
          (r, i) =>
            i !== index &&
            r.buttonType === 'CUSTOM' &&
            (r.buttonText ?? '').trim().toLowerCase() === currentText &&
            currentText !== ''
        );

        if (isDuplicate) {
          newErrors[index].buttonText = 'Quick reply button text must be unique.';
          // Also flag the other button(s) that share the same text
          newRows.forEach((r, i) => {
            if (
              i !== index &&
              r.buttonType === 'CUSTOM' &&
              (r.buttonText ?? '').trim().toLowerCase() === currentText
            ) {
              if (!newErrors[i]) {
                newErrors[i] = { buttonText: '', websiteUrl: '', phoneNumber: '', countryCode: '', copyOfferCode: '', general: '' };
              }
              newErrors[i].buttonText = 'Quick reply button text must be unique.';
            }
          });
        } else {
          // No duplicate — run standard button text validation
          newErrors[index].buttonText = validateButtonText(value, row.buttonType);
          // Also clear duplicate error on other CUSTOM buttons that may have been flagged
          newRows.forEach((r, i) => {
            if (i !== index && r.buttonType === 'CUSTOM') {
              const otherText = (r.buttonText ?? '').trim().toLowerCase();
              const stillDuplicate = newRows.some(
                (r2, i2) =>
                  i2 !== i &&
                  r2.buttonType === 'CUSTOM' &&
                  (r2.buttonText ?? '').trim().toLowerCase() === otherText &&
                  otherText !== ''
              );
              if (!stillDuplicate && newErrors[i]?.buttonText === 'Quick reply button text must be unique.') {
                newErrors[i].buttonText = '';
              }
            }
          });
        }
      } else {
        newErrors[index].buttonText = validateButtonText(value, row.buttonType);
      }
    } else if (field === 'websiteUrl') {
      newErrors[index].websiteUrl = validateWebsiteUrl(value);
    } else if (field === 'callPhoneNumber') {
      newErrors[index].phoneNumber = validatePhoneNumber(value);
    } else if (field === 'countryCode') {
      newErrors[index].countryCode = validateCountryCode(value, row.buttonType);
    } else if (field === 'copyOfferCode') {
      newErrors[index].copyOfferCode = validateCopyOfferCode(value);
    }

    setButtonValidationErrors(newErrors);
  };

  const handleButtonTypeChange = (index: number, newType: string) => {
    const newRows = [...rows];
    newRows[index] = {
      ...newRows[index],
      buttonType: newType,
      buttonTypeDropdown: newType,
      buttonText: newType === 'COPY_CODE' ? '' : newRows[index].buttonText,
      websiteUrl: "",
      countryCode: "",
      callPhoneNumber: "",
      copyOfferCode: "",
    };
    setRows(newRows);

    const newErrors = [...buttonValidationErrors];
    newErrors[index] = {
      buttonText: '',
      websiteUrl: '',
      phoneNumber: '',
      countryCode: '',
      copyOfferCode: '',
      general: ''
    };

    // ✅ Rule 1: When switching TO CUSTOM, check if existing text is already a duplicate
    if (newType === 'CUSTOM') {
      const currentText = (newRows[index].buttonText ?? '').trim().toLowerCase();
      if (currentText !== '') {
        const isDuplicate = newRows.some(
          (r, i) =>
            i !== index &&
            r.buttonType === 'CUSTOM' &&
            (r.buttonText ?? '').trim().toLowerCase() === currentText
        );
        if (isDuplicate) {
          newErrors[index].buttonText = 'Quick reply button text must be unique.';
        }
      }
    }

    setButtonValidationErrors(newErrors);
  };

  const onEmojiClick = (emojiObject: any) => {
    setBodyText(prev => prev + emojiObject.emoji);
    setShowPicker(false);
  };

  const insertFormattingTags = (startTag: string, endTag: string) => {
    const textarea = document.querySelector('textarea[name="bodyText"]') as HTMLTextAreaElement;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);
    const replacement = startTag + selectedText + endTag;
    const newValue = textarea.value.substring(0, start) + replacement + textarea.value.substring(end);
    handleBodyTextChange(newValue);
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + startTag.length, start + startTag.length + selectedText.length);
    }, 0);
  };
  const getBase64Src = (type: string) => {
    if (!mediaBase64) return null;
    if (mediaBase64.startsWith("data:")) return mediaBase64;
    if (type === "image") return `data:image/jpeg;base64,${mediaBase64}`;
    if (type === "video") return `data:video/mp4;base64,${mediaBase64}`;
    if (type === "document") return `data:application/pdf;base64,${mediaBase64}`;
    return "";
  };

  // ============================================================================
  // VALIDATION
  // ============================================================================

  const hasButtonValidationErrors = (): boolean => {
    return buttonValidationErrors.some(errors =>
      Object.values(errors).some(error => error !== '')
    );
  };

  const runSmsSequenceCheck = (val: string) => {
    const numbers = Array.from(val.matchAll(/\{\{(\d+)\}\}/g)).map((m) => Number(m[1]));
    const uniqueNumbers = Array.from(new Set(numbers)).sort((a, b) => a - b);
    const isSequential = uniqueNumbers.every((n, i) => n === i + 1);

    if (uniqueNumbers.length > 0 && !isSequential) {
      const expected = uniqueNumbers.map((_, i) => `{{${i + 1}}}`).join(", ");
      setMissingVariablesWarning(`Variables must be in sequence. Expected: ${expected}`);
    } else {
      // Only clear if no start-position error is active
      setMissingVariablesWarning("");
    }
  };


  const isFormValid = (): boolean => {
    if (!partnerId || !channelConfigId || !selectedChannelId) return false;
    if (!templateName.trim()) return false;
    if (!templateNameValid) return false;
    if (missingVariablesWarning) return false;

    if (selectedChannelId === 2) {
      return smsBody.trim() !== '' && !smsBodyError && !missingVariablesWarning;
    } else if (selectedChannelId === 1) {
      if (!language) return false;
      if (!bodyText || bodyText.trim().length < 25) return false;
      if (validationErrors.language) return false;
      if (validationErrors.bodyText) return false;
      if (validationErrors.headerText) return false;
      if (validationErrors.footerText) return false;
      if (validationErrors.headerContent) return false;
      if (validationErrors.mediaFile) return false;

      // Only check button validation for non-CUSTOM buttons
      const hasNonCustomErrors = buttonValidationErrors.some((errors, index) => {
        const row = rows[index];
        if (row.buttonType === 'CUSTOM') return false;
        return Object.values(errors).some(error => error !== '');
      });
      if (hasNonCustomErrors) return false;
    }

    return true;
  };

  const sendNotification = async (
    templateId: number,
    templateName: string,
    campaignType: string
  ) => {

    try {
      debugger;
      const payload = {
        workspaceId: partnerId,
        campaignId: templateId,   // ✅ pass templateId here
        campaignName: templateName,
        statusMark: "Unread",
        notificationType: "TemplateCreated",
        notificationData: {
          Message: `${workspaceName} created a Template - (${templateName})`
        },
        role: "Operator",
      };

      const response = await axios.post(
        `${apiUrlAdvAcc}/CreateNotification`,
        payload
      );

      if (response.data.status === "Success") {

      }
    } catch (e) {

    } finally {
      try {
        // const emailResponse = await axios.post(
        //   '${apiUrlAdvAcc}/SendEmailForOperatorCampaign',
        //   null, // No request body
        //   {
        //     params: {
        //       campaignId: campaignId,
        //     },
        //   }
        // );

      } catch (emailErr) {

      }
    }
  };

  const sendNotificationforresubmission = async (
    templateId: number,
    templateName: string,
    campaignType: string
  ) => {

    try {
      debugger;
      const payload = {
        workspaceId: partnerId,
        campaignId: templateId,   // ✅ pass templateId here
        campaignName: templateName,
        statusMark: "Unread",
        notificationType: "Templateresubmitted",
        notificationData: {
          Message: `${workspaceName} resubmitted a template - (${templateName})`,
        },
        role: "Operator",
      };

      const response = await axios.post(
        `${apiUrlAdvAcc}/CreateNotification`,
        payload
      );

      if (response.data.status === "Success") {

      }
    } catch (e) {

    } finally {
      try {
        // const emailResponse = await axios.post(
        //   '${apiUrlAdvAcc}/SendEmailForOperatorCampaign',
        //   null, // No request body
        //   {
        //     params: {
        //       campaignId: campaignId,
        //     },
        //   }
        // );

      } catch (emailErr) {

      }
    }
  };

const triggerTemplateCreationWebhookn8n = async (templateId: any,finalTemplateName: string, isCreation: boolean) => {
    try {
      const payload = {
        templateID: templateId,       // ✅ capital T — matches model field
        templateName: finalTemplateName,   // ✅ matches
        advertiserID: accountId,      // ✅ matches
        workspaceID: String(workspaceId),     // ✅ matches
        workspaceName: workspaceName, // ✅ matches
        request_type: isCreation
          ? "admin_create_template"
          : "admin_update_template", // ✅ matches
      };


      const response = await axios.post(`${adminUrl}/N8NAdvTemplateCreationandUpdationWebhook`, payload);
      if (response.status !== 200) {
        console.error("❌ Webhook Trigger Failed");
      } else {
        console.log("✅ Webhook Triggered Successfully");
      }
    } catch (error) {
      console.error("❗ Error in triggerN8NWebhook", error);
    }
  }

  const triggerN8NWebhook = async (
    templateId: any,
    isApproved: boolean
  ) => {
    try {
      const payload = {
        templateID: templateId,
        templateName: templateName,
        advertiserID: accountId?.toString(),
        workspaceID: workspaceId?.toString(), // ✅ add this — from props/redux
        wrokspaceName: "",
        request_type: isApproved
          ? "operator_approval_template"
          : "operator_reject_template",
        comments: "Approved this Template"
      };

      const response = await axios.post(
        `${operatorUrl}/N8NOperatorTemplateApprovalandRejectionWebhook`,
        payload,
      );

      if (response.status !== 200) {
        console.error("❌ Webhook Trigger Failed");
      } else {
        console.log("✅ Webhook Triggered Successfully");
      }
    } catch (error) {
      console.error("❗ Error in triggerN8NWebhook", error);
    }
  };

  const checkMissingVariables = (): boolean => {
    if (selectedChannelId === 1) {
      const unselected = variables.filter((v) => !v.value);
      if (unselected.length > 0) {
        setMissingVariablesWarning(`Please select a value for ${unselected.map(v => v.name).join(", ")}`);
        return true;
      }
    }
    if (selectedChannelId === 2) {
      const unselected = smsVariables.filter((v) => !v.value);
      if (unselected.length > 0) {
        setMissingVariablesWarning(`Please select a value for ${unselected.map(v => v.name).join(", ")}`);
        return true;
      }
    }
    setMissingVariablesWarning("");
    return false;
  };

  const insertAutoApprovalAudit = async (operatorWorkspaceId?: number, template_id?: number) => {
    try {
      const payload = {
        accountId: accountId, // same shape as Operator UI
        campaignId: template_id,
        operatorId: operatorWorkspaceId,
        status: "approved",
        approver: "Operator", // match Operator UI
        timestamp: new Date().toISOString(),
        rejectComment: "",
      };

      await axios.post(`${operatorUrl}/UpdateOperatorApprovalStatus`, payload);
    } catch (err) {
      console.error("❌ Failed to insert auto-approval operator audit:", err);
    }
  };

  const handleSubmit = async () => {
    if (checkMissingVariables()) return;

    // Validate variable position at start/end
    const activeBody = selectedChannelId === 2 ? smsBody : bodyText;
    const startsWithVar = /^\s*\{\{\d+\}\}/.test(activeBody);
    const endsWithVar = /\{\{\d+\}\}\s*$/.test(activeBody);
    if (selectedChannelId === 2) {
      // SMS: only block variable at start, allow at end
      if (startsWithVar) {
        toast.toast({ title: "Validation Error", description: "Variables cannot be placed at the start of the body text." });
        return;
      }
    } else {
      // WhatsApp: block variable at both start and end
      if (startsWithVar || endsWithVar) {
        toast.toast({ title: "Validation Error", description: "Variables cannot be placed at the start or end of the body text." });
        return;
      }
    }
    if (!isFormValid()) {
      toast.toast({
        title: "Validation Error",
        description: "Please fix all errors before submitting",
      });
      return;
    }

    setIsSubmitting(true);
    let ttl = 0;

    try {
      let payload: any = {};

      let configId = channelConfigId;

      if (selectedChannelId === 1) {
        const response = await axios.get(
          `${operatorUrl}/GetChannelConfigByConfigId`,
          { params: { configId } },
        );

        debugger;
        const details = response.data.channelConfigDetails;

        try {
          const parsedConfig =
            typeof details.channel_config === "string"
              ? JSON.parse(details.channel_config)
              : details.channel_config;

          ttl = Number(parsedConfig?.max_validity_hours ?? 0) * 3600;


        } catch (err) {
          console.error("Error parsing channel_config", err);
        }
      }


      const selectedPartner = partners.find(
        (p) => String(p.partnerId) === String(partnerId)
      );

      if (selectedChannelId === 2) {
        // SMS payload
        // const selectedPartner = partners.find(
        //   (p) => String(p.partnerId) === String(partnerId)
        // );

        if (!selectedPartner) {
          toast.toast({
            title: "Validation Error",
            description: "Please select an operator.",
          });
          setIsSubmitting(false);
          return;
        }

        const smsInfo = getSmsPartsCount(smsBody);

        const approval_flow_status = selectedPartner.autoApproval
          ? [
            {
              stage: "Operator",
              status: "approved",
              approver: "AUTO_APPROVER",
              timestamp: new Date().toISOString(),
            },
          ]
          : [
            {
              stage: "Operator",
              status: "pending",
              approver: null,
              timestamp: null,
            },
          ];

        payload = {
          template_name: templateName,
          channel_type: 2,
          target_type: "channel",
          target_id: Number(channelConfigId),
          approval_flow_status: approval_flow_status,
          ext_template_id: "",
          status: "inactive",
          user_account_id: Number(accountId),
          workspaceId: workspaceId,
          template_content: {
            channel_id: 2,
            part_count: smsInfo.parts,
            channel_type: "SMS",
            templateName: templateName,
            // ✅ REPLACE WITH THIS
            template: {
              name: templateName,
              message: smsBody,
              ...(smsVariables.length > 0 && {
                example: {
                  body_text: [
                    smsVariables.map((v) => v.value || v.name)
                  ]
                }
              }),
            },
          },
        };
      } else if (selectedChannelId === 1) {
        // // WhatsApp payload
        let media_config = null;

        if (selectedFile && mediaBase64) {
          media_config = {
            file_name: fileName,
            file_type: fileType,
            file_length: fileLength,
            binary_data: mediaBase64
          };
        }

        const components: any[] = [];

        // Header component
        // Header component
        if (headerType === "text" && textInput) {
          components.push({ type: "HEADER", format: "TEXT", text: textInput });
        } else if (["image", "video", "document"].includes(headerType) && media_config) {
          components.push({
            type: "HEADER",
            format: headerType.toUpperCase(),
            example: {
              header_handle: [""]
            }
          });
        }


        const bodyComponent: any = { type: "BODY", text: bodyText };

        if (variables.length > 0) {
          bodyComponent.example = {
            body_text: [
              variables.map((v) => v.value || v.name)  // ✅ stores "From_Country", "To_Country"
            ]
          };
        }
        components.push(bodyComponent);

        // Footer component
        if (footerText) {
          components.push({ type: "FOOTER", text: footerText });
        }

        const buttons = rows
          .filter(row =>
            row.buttonText.trim() ||
            (row.buttonType === 'COPY_CODE' && row.copyOfferCode.trim()) ||
            row.buttonType === 'CUSTOM'
          )
          .map(row => {
            if (row.buttonType === "URL") {
              return {
                type: "URL",
                text: row.buttonText,
                url: normalizeUrl(row.websiteUrl)
              };
            } else if (row.buttonType === "PHONE_NUMBER") {
              return {
                type: "PHONE_NUMBER",
                text: row.buttonText,
                phone_number: row.countryCode + row.callPhoneNumber
              };
            } else if (row.buttonType === "COPY_CODE") {
              return {
                type: "COPY_CODE",
                text: "Copy",  // Add this - WhatsApp requires text field
                example: [row.copyOfferCode]  // Change to array format
              };
            } else if (row.buttonType === "CUSTOM") {
              return {
                type: "QUICK_REPLY",
                text: row.buttonText,   // ✅ only text + type
              };
            }
            return {};
          });

        if (buttons.length > 0) {
          components.push({ type: "BUTTONS", buttons });
        }

        const template_content = {
          name: templateName,
          category: "MARKETING",
          language: language,
          components: components,

        };


        if (!selectedPartner) {
          toast.toast({
            title: "Validation Error",
            description: "Please select an operator.",
          });
          setIsSubmitting(false);
          return;
        }

        const approval_flow_status = selectedPartner.autoApproval
          ? [
            {
              stage: "Operator",
              status: "approved",
              approver: "AUTO_APPROVER",
              timestamp: new Date().toISOString(),
            },
            {
              stage: "Whatsapp",
              status: "pending",
              approver: null,
              timestamp: null,
            },
          ]
          : [
            {
              stage: "Operator",
              status: "pending",
              approver: null,
              timestamp: null,
            },
            {
              stage: "Whatsapp",
              status: "pending",
              approver: null,
              timestamp: null,
            },
          ];

        // ✅ REPLACE WITH THIS
        const qrArray = rows
          .filter((row) => row.buttonType === "CUSTOM" && row.buttonText.trim())
          .map((row) => {
            const entry: any = { label: row.buttonText };
            entry.opt_out = row.optOut ? (row.optOutType === "permanent" ? 2 : 1) : 0;
            entry.response = row.quickReplyResponse || "";
            return entry;
          });

        // ✅ Clean the components - remove opt_out and quick_reply_response from buttons
        const cleanedComponents = components.map((comp: any) => {
          if (comp.type === "BUTTONS") {
            return {
              ...comp,
              buttons: comp.buttons.map((btn: any) => {
                if (btn.type === "URL") {
                  return { type: btn.type, text: btn.text, url: btn.url };
                } else if (btn.type === "PHONE_NUMBER") {
                  return { type: btn.type, text: btn.text, phone_number: btn.phone_number };
                } else if (btn.type === "COPY_CODE") {
                  return { type: btn.type, text: btn.text, example: btn.example };
                } else {
                  // QUICK_REPLY — only text + type, no extra fields
                  return { type: btn.type, text: btn.text };
                }
              })
            };
          }
          return comp;
        });

        payload = {
          channel_type: 1,
          target_type: "channel",
          target_id: Number(channelConfigId),
          approval_flow_status: approval_flow_status,
          ext_template_id: "",
          status: "inactive",
          user_account_id: accountId,
          workspaceId: workspaceId,
          template_name: templateName,

          // ✅ QR at root level with extracted data


          // ✅ Clean template_content without opt_out and quick_reply_response in buttons
          template_content: {
            template: {
              name: templateName,
              category: "MARKETING",
              language: language,
              message_send_ttl_seconds: ttl,
              components: cleanedComponents,  // ✅ Use cleaned components
            },
            media_config: media_config,
            QR: qrArray
          },


        };

      }

      // Make API call
      const response = await axios.post(
        `${adminUrl}/InsertWelcomeTemplate`,
        payload,
        {
          headers: {
            "Content-Type": "application/json"
          }
        }
      );
      debugger

      if (response.data.status === "Success") {
        onSave(response.data);
        await triggerTemplateCreationWebhookn8n(response.data.template_id,response.data.template_name, false);

        if (selectedPartner.autoApproval === true) {
          await triggerN8NWebhook(response.data.template_id, true);

        }

        await sendNotification(
          response.data.template_id,
          templateName,
          "created"
        );
        onClose();
      } else {
        toast.toast({
          title: "Error",
          description: response.data.message || "Failed to submit template",
        });
      }
    } catch (error: any) {
      console.error("Error creating template:", error);
      toast.toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to submit template. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  //updatetemplate
  const handleUpdate = async () => {
    if (checkMissingVariables()) return;

    const activeBody = selectedChannelId === 2 ? smsBody : bodyText;
    const startsWithVar = /^\s*\{\{\d+\}\}/.test(activeBody);
    const endsWithVar = /\{\{\d+\}\}\s*$/.test(activeBody);

    if (selectedChannelId === 2) {
      if (startsWithVar) {
        toast.toast({
          title: "Validation Error",
          description: "Variables cannot be placed at the start of the body text."
        });
        return;
      }
    } else {
      if (startsWithVar || endsWithVar) {
        toast.toast({
          title: "Validation Error",
          description: "Variables cannot be placed at the start or end of the body text."
        });
        return;
      }
    }

    if (!isFormValid()) {
      toast.toast({
        title: "Validation Error",
        description: "Please fix all errors before updating",
      });
      return;
    }

    setIsSubmitting(true);

    let ttl = 0;

    try {
      let configId = channelConfigId;

      if (selectedChannelId === 1) {
        const response = await axios.get(
          `${operatorUrl}/GetChannelConfigByConfigId`,
          { params: { configId } }
        );

        const details = response.data.channelConfigDetails;

        try {
          const parsedConfig =
            typeof details.channel_config === "string"
              ? JSON.parse(details.channel_config)
              : details.channel_config;

          ttl = Number(parsedConfig?.max_validity_hours ?? 0) * 3600;
        } catch (err) {
          console.error("Error parsing channel_config", err);
        }
      }

      const selectedPartner = partners.find(
        (p) => String(p.partnerId) === String(partnerId)
      );

      type ApprovalFlowStage = {
        stage: string;
        status: string;
        approver: string | null;
        timestamp: string | null;
      };

      let updatedApprovalFlow: ApprovalFlowStage[] = [];
      let updatedTemplateContent: any = {};

      if (selectedChannelId === 2) {
        const smsInfo = getSmsPartsCount(smsBody);

        updatedTemplateContent = {
          channel_id: 2,
          part_count: smsInfo.parts,
          channel_type: "SMS",
          templateName: templateName,
          template: {
            name: templateName,
            message: smsBody,
            ...(smsVariables.length > 0 && {
              example: {
                body_text: [smsVariables.map((v) => v.value || v.name)],
              },
            }),
          },
        };

        updatedApprovalFlow = selectedPartner?.autoApproval
          ? [
            {
              stage: "Operator",
              status: "approved",
              approver: "AUTO_APPROVER",
              timestamp: new Date().toISOString(),
            },
          ]
          : [
            {
              stage: "Operator",
              status: "pending",
              approver: null,
              timestamp: null,
            },
          ];
      } else if (selectedChannelId === 1) {
        let media_config = null;

        if (selectedFile && mediaBase64) {
          media_config = {
            file_name: fileName,
            file_type: fileType,
            file_length: fileLength,
            binary_data: mediaBase64,
          };
        } else if (mediaBase64) {
          media_config = {
            file_name: fileName,
            file_type: fileType,
            binary_data: mediaBase64,
          };
        }

        const components: any[] = [];

        if (headerType === "text" && textInput) {
          components.push({ type: "HEADER", format: "TEXT", text: textInput });
        } else if (["image", "video", "document"].includes(headerType) && media_config) {
          components.push({
            type: "HEADER",
            format: headerType.toUpperCase(),
            example: { header_handle: [""] },
          });
        }

        const bodyComponent: any = { type: "BODY", text: bodyText };

        if (variables.length > 0) {
          bodyComponent.example = {
            body_text: [variables.map((v) => v.value || v.name)],
          };
        }
        components.push(bodyComponent);

        if (footerText) {
          components.push({ type: "FOOTER", text: footerText });
        }

        const qrArray = rows
          .filter((row) => row.buttonType === "CUSTOM" && row.buttonText.trim())
          .map((row) => {
            const entry: any = { label: row.buttonText };
            entry.opt_out = row.optOut ? (row.optOutType === "permanent" ? 2 : 1) : 0;
            entry.response = row.quickReplyResponse || "";
            return entry;
          });

        const buttons = rows
          .filter(
            (row) =>
              row.buttonText.trim() ||
              (row.buttonType === "COPY_CODE" && row.copyOfferCode.trim()) ||
              row.buttonType === "CUSTOM"
          )
          .map((row) => {
            if (row.buttonType === "URL") {
              return {
                type: "URL",
                text: row.buttonText,
                url: normalizeUrl(row.websiteUrl),
              };
            } else if (row.buttonType === "PHONE_NUMBER") {
              return {
                type: "PHONE_NUMBER",
                text: row.buttonText,
                phone_number: row.countryCode + row.callPhoneNumber,
              };
            } else if (row.buttonType === "COPY_CODE") {
              return {
                type: "COPY_CODE",
                text: "Copy",
                example: [row.copyOfferCode],
              };
              // } else if (row.buttonType === "CUSTOM") {
              //   return {
              //     type: "QUICK_REPLY",
              //     text: row.buttonText,
              //     quick_reply_response: row.quickReplyResponse || "",
              //     opt_out: row.optOut || false,
              //   };
            } else if (row.buttonType === "CUSTOM") {
              return {
                type: "QUICK_REPLY",
                text: row.buttonText,
              };
            }
            return {};
          });

        if (buttons.length > 0) {
          components.push({ type: "BUTTONS", buttons });
        }

        updatedTemplateContent = {
          template: {
            name: templateName,
            category: "MARKETING",
            language: language,
            message_send_ttl_seconds: ttl,
            components: components,
          },
          media_config: media_config,
          QR: qrArray,
        };

        updatedApprovalFlow = selectedPartner?.autoApproval
          ? [
            {
              stage: "Operator",
              status: "approved",
              approver: "AUTO_APPROVER",
              timestamp: new Date().toISOString(),
            },
            {
              stage: "Whatsapp",
              status: "pending",
              approver: null,
              timestamp: null,
            },
          ]
          : [
            {
              stage: "Operator",
              status: "pending",
              approver: null,
              timestamp: null,
            },
            {
              stage: "Whatsapp",
              status: "pending",
              approver: null,
              timestamp: null,
            },
          ];
      }

      const payload = {
        template_id: initialContent.template_id,
        template_content: JSON.stringify(updatedTemplateContent),
        status: "inactive",
        user_account_id: Number(accountId),
        advertiser_workspace_id: Number(workspaceId),
        approval_flow_status: updatedApprovalFlow,
      };

      const response = await axios.put(
        `${adminUrl}/UpdateTemplate`,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );

      await triggerTemplateCreationWebhookn8n(response.data.template_id,response.data.template_name, false);
      if (selectedPartner.autoApproval == true) {
        await triggerN8NWebhook(response.data.template_id, true)
      }
      await sendNotificationforresubmission(
        response.data.template_id,
        templateName,
        "created"
      );

      if (response.data.status === "Success") {
        toast.toast({
          title: "Success",
          description: "Template updated successfully.",
        });
        onSave(response.data);
        onClose();
      } else {
        toast.toast({
          title: "Error",
          description: response.data.message || "Failed to update template.",
        });
      }
    } catch (error: any) {
      console.error("Error updating template:", error);
      toast.toast({
        title: "Error",
        description:
          error.response?.data?.message || "Failed to update template. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };


  // ============================================================================
  // RENDER HELPERS
  // ============================================================================

  const noContentSelected = selectedChannelId === 1
    ? (!bodyText && !textInput && !selectedFile && !mediaBase64)
    : !smsBody;
  const maxLength = 60;

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div>
      {/* Main layout: left = form, right = preview */}
      <div className="flex flex-col md:flex-row gap-6 mb-6 mt-4">
        {/* Left: Form fields */}
        <div className="space-y-6 w-full max-w-[650px] md:w-3/5">

          {/* Configuration Card */}
          <Card className="border p-4 rounded-lg text-left h-auto">
            <h2 className="text-base font-semibold mb-2">Configuration</h2>

            <div className="flex-shrink-0">
              <Label htmlFor="operator">Operator</Label>
              {mode === 'view' || mode === 'edit' ? (
                <Input
                  value={initialContent?.partner_name || ''}
                  disabled
                  className="bg-gray-50 mt-2"
                />
              ) : (
                <Select
                  value={partnerId}
                  onValueChange={handlePartnerChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Operator" />
                  </SelectTrigger>
                  <SelectContent>
                    <div className="flex items-center px-3 pb-2 border-b">
                      <svg
                        className="w-4 h-4 text-gray-400 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                      <input
                        type="text"
                        placeholder="Search..."
                        value={operatorSearchQuery}
                        onChange={(e) => setOperatorSearchQuery(e.target.value)}
                        className="flex-1 outline-none text-sm"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="max-h-[200px] overflow-y-auto">
                      {partners?.length > 0 ? (
                        partners
                          .filter(p =>
                            p.partnerName.toLowerCase().includes(operatorSearchQuery.toLowerCase())
                          )
                          .map((p) => (
                            <SelectItem key={p.partnerId} value={String(p.partnerId)}>
                              {p.partnerName}
                            </SelectItem>
                          ))
                      ) : (
                        <div className="px-3 py-2 text-sm text-gray-500">
                          No operators available
                        </div>
                      )}
                    </div>
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="flex-shrink-0 mt-2">
              <Label htmlFor="config">Operator Config</Label>
              {mode === 'view' || mode === 'edit' ? (
                <Input
                  value={initialContent?.channel_config_name || ''}
                  disabled
                  className="bg-gray-50 mt-2"
                />
              ) : (
                <Select
                  value={channelConfigId}
                  onValueChange={handleConfigChange}
                  disabled={!partnerId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Operator Config" />
                  </SelectTrigger>
                  <SelectContent>
                    <div className="flex items-center px-3 pb-2 border-b">
                      <svg
                        className="w-4 h-4 text-gray-400 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                      <input
                        type="text"
                        placeholder="Search..."
                        value={configSearchQuery}
                        onChange={(e) => setConfigSearchQuery(e.target.value)}
                        className="flex-1 outline-none text-sm"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="max-h-[200px] overflow-y-auto">
                      {configs.length > 0 ? (
                        configs
                          .filter(c =>
                            c.channelConfigName.toLowerCase().includes(configSearchQuery.toLowerCase())
                          )
                          .map((c) => (
                            <SelectItem key={c.channelConfigId} value={c.channelConfigId.toString()}>
                              {c.channelConfigName}
                            </SelectItem>
                          ))
                      ) : (
                        <div className="px-3 py-2 text-sm text-gray-500">
                          No configs available
                        </div>
                      )}
                    </div>
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Template Name - shown for SMS */}
            {selectedChannelId === 2 && (
              <div className="flex-shrink-0 mt-2">
                <Label htmlFor="smsTemplateName">Template Name</Label>
                <Input
                  id="smsTemplateName"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="Enter template name"
                  className="mt-2"
                  disabled={mode === 'view' || mode === 'edit'}
                />

              </div>
            )}
          </Card>

          {/* SMS Content - Shows when channelId === 2 */}
          {selectedChannelId === 2 && (
            <Card className="border p-4 rounded-lg text-left h-auto">
              <h2 className="text-base font-semibold mb-2">Content</h2>

              <div className="space-y-4 mt-6">
                <div>
                  <Label htmlFor="bodyText">Body</Label>
                  {showSmsWarning && (
                    <Card className="flex items-center justify-between border text-black-100 rounded-md px-4 py-4 mt-4 text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-500" />
                        <span>
                          This content will consume {partsCount} SMS messages.
                        </span>
                      </div>

                      <HoverCard>
                        <HoverCardTrigger asChild>
                          <span className="cursor-pointer">
                            <Info
                              className="w-4 h-4 cursor-pointer hover:opacity-80"
                              style={{ color: "#3A85F7" }}
                            />
                          </span>
                        </HoverCardTrigger>
                        <HoverCardContent className="w-80 p-4 text-sm bg-white shadow-lg border border-gray-200 rounded-xl z-50">
                          <p className="font-semibold text-gray-600 mb-2">
                            Why is the message split?
                          </p>
                          <p className="text-gray-500 mb-2">
                            Most messages use a standard encoding called{" "}
                            <strong>GSM-7</strong>, which allows up to{" "}
                            <strong>160 characters</strong> in one SMS.
                          </p>
                          <p className="text-gray-500 mb-2">
                            If your message includes special symbols, emojis, or
                            characters from non-English languages, it switches
                            to a different encoding called{" "}
                            <strong>UCS-2</strong>, which allows only{" "}
                            <strong>70 characters</strong> per SMS.
                          </p>
                          <p className="text-gray-500">
                            When your message goes beyond these limits, it is
                            automatically split into multiple parts.
                          </p>
                        </HoverCardContent>
                      </HoverCard>
                    </Card>
                  )}
                  <div className="relative mt-4">
                    <div className="text-xs text-gray-500 mt-1 flex justify-end mb-2">
                      Character Count: {smsBody.length}
                    </div>

                    <textarea
                      name="smsBody"
                      className="w-full h-[150px] border rounded p-2 text-[#020617] resize-none overflow-y-auto overflow-x-hidden break-all"
                      value={smsBody}
                      placeholder="Hello"
                      // onChange={(e) => {
                      //   setSmsBody(e.target.value);
                      //   setMissingVariablesWarning("");  // ← ADD
                      // }}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSmsBody(val);
                        const smsInfo = getSmsPartsCount(val);
                        setPartsCount(smsInfo.parts);
                        setShowSmsWarning(smsInfo.parts > 1);

                        // Auto-remove pills whose tag was deleted from body
                        const tagsInBody = Array.from(val.matchAll(/\{\{(\d+)\}\}/g)).map((m) => m[0].trim());
                        const updatedVars = smsVariables.filter((v) => tagsInBody.includes(v.name.trim()));
                        setSmsVariables(updatedVars);

                        // Start-position check — takes priority; if triggered, skip sequence check
                        const startsWithVar = /^\s*\{\{\d+\}\}/.test(val);
                        if (startsWithVar) {
                          setSmsBodyError("Variables cannot be placed at the start of the body text.");
                          setMissingVariablesWarning(""); // clear sequence warning while start error is active
                        } else {
                          setSmsBodyError(""); // clear start error as soon as it's no longer at start
                          runSmsSequenceCheck(val); // now check sequence with the updated body
                        }
                      }}
                      disabled={mode === "view"}
                    />
                    <div className="flex items-center">
                      {/* Error message → LEFT */}
                      {smsBodyError && (
                        <p className="text-red-500 text-xs font-medium mt-1 font-sans italic ml-1">
                          {smsBodyError}
                        </p>
                      )}

                      {missingVariablesWarning && (
                        <p className="text-red-500 text-xs font-medium mt-1 font-sans italic ml-1">
                          {missingVariablesWarning}
                        </p>
                      )}

                      {mode !== "view" && smsVariables.length < 3 && (
                        <button
                          type="button"
                          className="ml-auto flex items-center gap-1 text-xs text-[#6c7176] border border-[#979ba0] rounded px-2 py-0 h-5 hover:bg-blue-50 whitespace-nowrap leading-none"
                          onClick={() => {
                            const textarea = document.querySelector('textarea[name="smsBody"]') as HTMLTextAreaElement;
                            const usedNumbers = [1, 2, 3].filter((n) => smsBody.includes(`{{${n}}}`));
                            const nextNum = [1, 2, 3].find((n) => !usedNumbers.includes(n)) || 1;
                            const varName = ` {{${nextNum}}} `;
                            let newBody = smsBody;  // ✅ initialized correctly here
                            if (textarea) {
                              const start = textarea.selectionStart;
                              const end = textarea.selectionEnd;
                              newBody = smsBody.substring(0, start) + varName + smsBody.substring(end); // ✅ updated
                              setSmsBody(newBody);
                              setTimeout(() => {
                                textarea.focus();
                                textarea.setSelectionRange(start + varName.length, start + varName.length);
                              }, 0);
                            } else {
                              newBody = smsBody + varName;  // ✅ updated
                              setSmsBody(newBody);
                            }
                            setSmsVariables((prev) => [...prev, { name: varName, value: "" }]);
                            setNextSmsVarIndex((prev) => prev + 1);
                            runSmsSequenceCheck(newBody);  // ✅ correct — newBody is updated above
                          }}
                        >
                          <PlusIcon className="w-3 h-3" />
                          Add Variable
                        </button>
                      )}
                    </div>

                    {/* ✅ Variable rows */}
                    {smsVariables.length > 0 && (
                      <div className="mt-3 space-y-3">
                        {smsVariables.map((variable, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            {/* {{1}} pill */}
                            <div className="flex-shrink-0 w-[70px] h-9 flex items-center justify-center bg-gray-100 border rounded text-sm text-gray-600 font-medium">
                              {variable.name}
                            </div>

                            {/* Dropdown */}
                            <div className="flex-1">
                              <Select
                                value={variable.value}
                                onValueChange={(val) => {
                                  const updated = [...smsVariables];
                                  updated[idx] = {
                                    ...updated[idx],
                                    value: val,
                                  };
                                  setSmsVariables(updated);
                                }}
                                disabled={mode === "view"}
                              >
                                <SelectTrigger
                                  className={`text-[#020617] h-9 ${mode === "view" ? "bg-gray-50 cursor-not-allowed" : ""}`}
                                >
                                  <SelectValue
                                    placeholder={`Enter content for ${variable.name}`}
                                  />
                                </SelectTrigger>
                                <SelectContent>
                                  {VARIABLE_OPTIONS_SMS.map((opt) => (
                                    <SelectItem key={opt} value={opt}>
                                      {opt}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            {/* Trash button */}
                            {mode !== "view" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-700 hover:bg-red-50 w-8 px-0 mt-2"
                                onClick={() => {
                                  const removed = smsVariables[idx];
                                  const updatedBody = smsBody.replace(removed.name, "");
                                  const updatedVars = smsVariables.filter((_, i) => i !== idx);

                                  setSmsBody(updatedBody);
                                  setSmsVariables(updatedVars);

                                  // Clear start-position error if body no longer starts with a variable
                                  const startsWithVar = /^\s*\{\{\d+\}\}/.test(updatedBody);
                                  if (!startsWithVar) {
                                    setSmsBodyError("");
                                  }

                                  // Run sequence check on the updated body
                                  const numbers = Array.from(updatedBody.matchAll(/\{\{(\d+)\}\}/g)).map((m) => Number(m[1]));
                                  const uniqueNumbers = Array.from(new Set(numbers)).sort((a, b) => a - b);
                                  const isSequential = uniqueNumbers.every((n, i) => n === i + 1);

                                  if (uniqueNumbers.length > 0 && !isSequential) {
                                    const expected = uniqueNumbers.map((_, i) => `{{${i + 1}}}`).join(", ");
                                    setMissingVariablesWarning(`Variables must be in sequence. Expected: ${expected}`);
                                  } else {
                                    setMissingVariablesWarning("");
                                  }
                                }}
                              >
                                <FaTrash className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* WhatsApp Content - Shows when channelId === 1 */}
          {selectedChannelId === 1 && (
            <Card className="border p-4 rounded-lg text-left">
              <h2 className="text-base font-semibold mb-2">Content</h2>

              <div className="flex-shrink-0">
                <Label htmlFor="language">Language</Label>
                <Select
                  value={language}
                  onValueChange={handleLanguageChange}
                  disabled={languagesLoading || mode === 'view'}
                >
                  <SelectTrigger className={`text-[#020617] w-1/2 mt-2 ${validationErrors.language ? 'border-red-500' : ''}`}>
                    <SelectValue placeholder={languagesLoading ? "Loading..." : "Select Language"} />
                  </SelectTrigger>
                  <SelectContent className="h-48">
                    {languages.map((option: LanguageOption) => (
                      <SelectItem key={option.value} value={option.value} className="cursor-pointer">
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {validationErrors.language && (
                  <p className="text-red-500 font-medium text-xs mt-1 italic">{validationErrors.language}</p>
                )}
              </div>

              {/* Template Name */}
              <div className="flex flex-col gap-1 mt-4">
                <Label htmlFor="templateName">Template Name</Label>
                <Input
                  id="templateName"
                  type="text"
                  placeholder="e.g. campaign_template_1"
                  value={templateName}
                  onChange={(e) => {
                    const value = e.target.value;
                    const regex = /^[a-z][a-z0-9_]*$/;
                    setTemplateName(value);
                    setTemplateNameValid(regex.test(value) || value === "");
                  }}
                  className={templateNameValid ? "" : "border-red-500"}
                  disabled={mode === 'view' || mode === 'edit'}
                />
                {!templateNameValid && (
                  <p className="text-sm text-red-500">
                    Template name must start with a lowercase letter and contain only
                    lowercase letters, numbers, and underscore (_).
                  </p>
                )}
              </div>

              <div className="space-y-4 mt-4">
                {/* Header */}
                <div>
                  <Label>Header</Label>
                  <div
                    style={{
                      background: "#F0F4F8",
                      color: "#64748B",
                      padding: "2px 10px",
                      borderRadius: "9999px",
                      display: "inline-flex",
                      alignItems: "center",
                      height: "20px",
                      marginLeft: "8px",
                      fontSize: "14px",
                      fontWeight: 600,
                    }}
                  >
                    Optional
                  </div>
                  <Select
                    value={headerType}
                    onValueChange={handleHeaderTypeChange}
                    disabled={mode === 'view'}
                  >
                    <SelectTrigger className="w-full text-[#020617] mt-2">
                      <SelectValue placeholder="Select Header Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="text">Text</SelectItem>
                      <SelectItem value="image">Image</SelectItem>
                      <SelectItem value="video">Video</SelectItem>
                      <SelectItem value="document">Document</SelectItem>
                    </SelectContent>
                  </Select>
                  {validationErrors.headerContent && (
                    <p className="text-red-500 font-medium text-xs mt-1 italic">{validationErrors.headerContent}</p>
                  )}
                </div>

                {/* Header Text Input */}
                {headerType === "text" && (
                  <div>
                    <div className="flex justify-between">
                      <Label>Header Text</Label>
                      <div className="text-xs text-gray-500 mt-0">{textInput.length}/{maxLength}</div>
                    </div>
                    <Input
                      value={textInput}
                      onChange={(e) => handleHeaderTextChange(e.target.value)}
                      placeholder="Enter header text"
                      className={`mt-2 text-[#020617] ${validationErrors.headerText ? 'border-red-500' : ''}`}
                      maxLength={60}
                      disabled={mode === 'view' || (!!mediaBase64 && mode === 'create')}
                    />
                    {validationErrors.headerText && (
                      <p className="text-red-500 font-medium text-xs mt-1 italic">
                        {validationErrors.headerText}
                      </p>
                    )}
                  </div>
                )}

                {/* Media Upload */}
                {["image", "video", "document"].includes(headerType) && (
                  <div>
                    <Label>Upload {headerType}</Label>
                    <div className="mt-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        onChange={handleFileChange}
                        accept={
                          headerType === "image"
                            ? "image/jpeg,image/png"
                            : headerType === "video"
                              ? "video/mp4"
                              : "application/pdf"
                        }
                        className="hidden"
                        disabled={mode === 'view' || (!!mediaBase64 && mode === 'create')}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full border-dashed border-2 h-20 text-[#020617]"
                        disabled={mode === 'view' || (!!mediaBase64 && mode === 'create')}
                      >
                        {selectedFile ? (
                          <div className="text-center">
                            <div className="font-medium">{selectedFile.name}</div>
                            <div className="text-sm text-gray-500">
                              {(selectedFile.size / 1024).toFixed(1)} KB
                            </div>
                          </div>
                        ) : (
                          <div className="text-center">
                            <PlusIcon className="mx-auto h-6 w-6 mb-2" />
                            {`Click to upload ${headerType}`}
                          </div>
                        )}
                      </Button>
                      {uploadError && (
                        <p className="text-red-500 italic text-xs mt-1">{uploadError}</p>
                      )}
                      {validationErrors.mediaFile && (
                        <p className="text-red-500 font-medium text-xs mt-1 italic">
                          {validationErrors.mediaFile}
                        </p>
                      )}
                      {(selectedFile || mediaBase64) && (
                        <div className="flex justify-end">
                          <Button
                            type="button"
                            variant="ghost"
                            onClick={() => {
                              setSelectedFile(null);
                              setPreviewImage(null);
                              setMediaBase64('');
                              setFileName('');
                              setFileType('');
                              setFileLength('');
                              if (fileInputRef.current) fileInputRef.current.value = '';
                              setValidationErrors(prev => ({
                                ...prev,
                                mediaFile: '',
                                headerContent: validateHeaderContent(headerType, textInput, false)
                              }));
                            }}
                            className="text-red-500 mt-1 w-30 hover:text-red-700 hover:bg-red-50 flex justify-end"
                            disabled={mode === 'view'}
                          >
                            <FaTrash className="mr-2" />
                            Remove file
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Body Text */}
                <div>
                  <div className="flex justify-between">
                    <Label>Body</Label>
                    <div className="text-xs text-gray-500 mb-1 flex justify-end">{bodyText.length}/1024</div>
                  </div>
                  <div className="relative mt-2">
                    <Textarea
                      name="bodyText"
                      className={`w-full border rounded p-2 text-[#020617] pr-14 min-h-[120px] max-h-[120px] overflow-y-auto resize-none ${validationErrors.bodyText || missingVariablesWarning ? 'border-red-500' : ''}`}
                      maxLength={1024}
                      value={bodyText}
                      placeholder="Hello"
                      onChange={(e) => handleBodyTextChange(e.target.value)}
                      disabled={mode === 'view'}
                    />
                    <div className="absolute mt-2 right-2 flex gap-2">
                      <Smile
                        className="w-4 h-4 cursor-pointer text-gray-600 mb-4 hover:text-gray-800"
                        onClick={() => setShowPicker(!showPicker)}
                      />
                      <FontBoldIcon
                        className="w-4 h-4 cursor-pointer text-gray-600 hover:text-gray-800"
                        onClick={() => insertFormattingTags("*", "*")}
                      />
                      <FontItalicIcon
                        className="w-4 h-4 cursor-pointer text-gray-600 hover:text-gray-800"
                        onClick={() => insertFormattingTags("_", "_")}
                      />
                      <Strikethrough
                        className="w-4 h-4 cursor-pointer text-gray-600 hover:text-gray-800"
                        onClick={() => insertFormattingTags("~", "~")}
                      />
                      <CodeIcon
                        className="w-4 h-4 cursor-pointer text-gray-600 hover:text-gray-800"
                        onClick={() => insertFormattingTags("`", "`")}
                      />
                      {mode !== 'view' && variables.length < 2 && (
                        <button
                          type="button"
                          className="flex items-center gap-1 text-xs text-[#6c7176] border border-[#979ba0] rounded px-2 py-0 h-5 hover:bg-blue-50 whitespace-nowrap leading-none"
                          onClick={() => {
                            const textarea = document.querySelector('textarea[name="bodyText"]') as HTMLTextAreaElement;
                            const usedNumbers = [1, 2].filter((n) => bodyText.includes(`{{${n}}}`));
                            const nextNum = [1, 2].find((n) => !usedNumbers.includes(n)) || 1;
                            const varName = ` {{${nextNum}}} `;
                            if (textarea) {
                              const start = textarea.selectionStart;
                              const end = textarea.selectionEnd;
                              const newValue = bodyText.substring(0, start) + varName + bodyText.substring(end);
                              handleBodyTextChange(newValue);
                              setTimeout(() => {
                                textarea.focus();
                                textarea.setSelectionRange(start + varName.length, start + varName.length);
                              }, 0);
                            } else {
                              handleBodyTextChange(bodyText + varName);
                            }
                            setVariables((prev) => [...prev, { name: varName, value: "" }]);
                            setNextVarIndex((prev) => prev + 1);
                          }}
                        >
                          <PlusIcon className="w-3 h-3" />
                          Add Variable
                        </button>
                      )}
                    </div>

                    {showPicker && (
                      <div className="absolute top-full left-0 z-50 mt-2">
                        <EmojiPicker onEmojiClick={onEmojiClick} />
                      </div>
                    )}
                  </div>
                  {validationErrors.bodyText && (
                    <p className="text-red-500 font-medium text-xs mt-1 italic">{validationErrors.bodyText}</p>
                  )}
                  {missingVariablesWarning && (
                    <p className="text-red-500 font-medium text-xs mt-1 italic">{missingVariablesWarning}</p>
                  )}

                  {/* Variable Rows */}
                  {variables.length > 0 && (
                    <div className="mt-10 space-y-3">
                      {variables.map((variable, idx) => (
                        <div key={idx} className="flex items-start gap-4">
                          <div className="flex-shrink-0 w-[70px] h-9 flex items-center justify-center bg-gray-100 border rounded text-sm text-gray-600 font-medium">
                            {variable.name}
                          </div>
                          <div className="flex-1 flex flex-col gap-1">
                            <Select
                              value={variable.value}
                              onValueChange={(val) => {
                                const updated = [...variables];
                                updated[idx] = { ...updated[idx], value: val };
                                setVariables(updated);
                              }}
                              disabled={mode === 'view'}
                            >
                              <SelectTrigger
                                className={`text-[#020617] h-9 ${mode === 'view' ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                              >
                                <SelectValue placeholder={`Enter content for ${variable.name}`} />
                              </SelectTrigger>
                              <SelectContent>
                                {VARIABLE_OPTIONS_WHATSAPP.map((opt) => (
                                  <SelectItem key={opt} value={opt}>
                                    {opt}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          {mode !== 'view' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 mt-0 hover:text-red-700 hover:bg-red-50 w-8 px-0"
                              // ✅ CORRECT — uses variables/bodyText for WhatsApp
                              onClick={() => {
                                const removed = variables[idx];
                                const updatedBody = bodyText.replace(removed.name, "");
                                setBodyText(updatedBody);
                                setVariables((prev) => prev.filter((_, i) => i !== idx));
                                setMissingVariablesWarning("");
                              }}
                            >
                              <FaTrash className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div>
                  <div className="flex justify-between mt-8">
                    <div>
                      <Label>Footer</Label>
                      <div
                        style={{
                          background: "#F0F4F8",
                          color: "#64748B",
                          padding: "2px 10px",
                          borderRadius: "9999px",
                          display: "inline-flex",
                          alignItems: "center",
                          height: "20px",
                          marginLeft: "8px",
                          fontSize: "14px",
                          fontWeight: 600,
                        }}
                      >
                        Optional
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 mt-0 flex justify-end">{footerText.length}/60</div>
                  </div>
                  <Input
                    value={footerText}
                    onChange={(e) => handleFooterTextChange(e.target.value)}
                    placeholder="Enter footer text"
                    className={`mt-2 text-[#020617] ${validationErrors.footerText ? 'border-red-500' : ''}`}
                    maxLength={60}
                    disabled={mode === 'view'}
                  />
                  {validationErrors.footerText && (
                    <p className="text-red-500 font-medium text-xs mt-1 italic">{validationErrors.footerText}</p>
                  )}
                </div>

                {/* Buttons */}
                <div>
                  <Label className="text-base font-semibold">Buttons</Label>
                  <div
                    style={{
                      background: "#F0F4F8",
                      color: "#64748B",
                      padding: "2px 10px",
                      borderRadius: "9999px",
                      display: "inline-flex",
                      alignItems: "center",
                      height: "20px",
                      marginLeft: "8px",
                      fontSize: "14px",
                      fontWeight: 600,
                    }}
                  >
                    Optional
                  </div>
                  <div className="space-y-3 mt-2">
                    {rows.map((row, index) => {

                      // ✅ Rule 2: Disable optOut checkbox on this row if any OTHER
                      // CUSTOM row already has optOut checked
                      // const anyOtherOptOutChecked = rows.some(
                      //   (r, i) =>
                      //     i !== index &&
                      //     r.buttonType === 'CUSTOM' &&
                      //     r.optOut === true
                      // );

                      return (
                        <div key={index} className="border rounded-lg p-4 bg-gray-50">
                          <div className="flex flex-col space-y-3">
                            <div className="flex flex-col md:flex-row gap-3">
                              <div className="flex-1">
                                <Label className="text-sm">Action type</Label>
                                <Select
                                  value={row.buttonType}
                                  onValueChange={(value) => handleButtonTypeChange(index, value)}
                                  disabled={mode === 'view'}
                                >
                                  <SelectTrigger className="text-[#020617] mt-1">
                                    <SelectValue placeholder="Select action type" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="URL">View Website</SelectItem>
                                    <SelectItem value="PHONE_NUMBER">Call Phone Number</SelectItem>
                                    <SelectItem value="COPY_CODE">Copy Offer Code</SelectItem>
                                    <SelectItem value="CUSTOM">Quick Reply</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="flex-1">
                                <div className="flex justify-between">
                                  <Label className="text-sm">Button text</Label>
                                  {(row.buttonType === "URL" || row.buttonType === "PHONE_NUMBER" || row.buttonType === "COPY_CODE") && (
                                    <div className="text-xs text-gray-500 mt-0">
                                      {row.buttonType === "COPY_CODE" ? "4" : row.buttonText.length}/25
                                    </div>
                                  )}
                                </div>
                                <Input
                                  className={`text-[#020617] mt-1 ${buttonValidationErrors[index]?.buttonText ? 'border-red-500' : ''}`}
                                  placeholder="Button text"
                                  value={row.buttonType === "COPY_CODE" ? "Copy" : row.buttonText}
                                  onChange={(e) => updateButtonRow(index, "buttonText", e.target.value)}
                                  maxLength={25}
                                  disabled={row.buttonType === "COPY_CODE" || mode === 'view'}
                                />
                                {buttonValidationErrors[index]?.buttonText && (
                                  <p className="text-red-500 font-medium text-xs mt-1 italic">
                                    {buttonValidationErrors[index].buttonText}
                                  </p>
                                )}
                              </div>
                            </div>

                            {row.buttonType === 'URL' && (
                              <div>
                                <Label className="text-sm">Website URL</Label>
                                <Input
                                  className={`text-[#020617] mt-1 ${buttonValidationErrors[index]?.websiteUrl ? 'border-red-500' : ''}`}
                                  placeholder="https://example.com"
                                  value={row.websiteUrl}
                                  onChange={(e) => updateButtonRow(index, "websiteUrl", e.target.value)}
                                  disabled={mode === 'view'}
                                />
                                {buttonValidationErrors[index]?.websiteUrl && (
                                  <p className="text-red-500 font-medium text-xs mt-1 italic">
                                    {buttonValidationErrors[index].websiteUrl}
                                  </p>
                                )}
                              </div>
                            )}

                            {row.buttonType === 'PHONE_NUMBER' && (
                              <div className="flex gap-3 items-start">
                                <div className="w-[160px]">
                                  <Label className="text-sm">Country Code</Label>
                                  <Select
                                    value={row.countryCode}
                                    onValueChange={(value) => updateButtonRow(index, "countryCode", value)}
                                    disabled={countryCodesLoading || mode === 'view'}
                                  >
                                    <SelectTrigger className={`text-[#020617] mt-1 ${buttonValidationErrors[index]?.countryCode ? 'border-red-500' : ''}`}>
                                      <SelectValue placeholder={countryCodesLoading ? "Loading..." : "Select Code"} />
                                    </SelectTrigger>
                                    <SelectContent className="h-48">
                                      {countryCodes.map((cc) => (
                                        <SelectItem key={cc.value} value={cc.value} className="cursor-pointer">
                                          {cc.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  {buttonValidationErrors[index]?.countryCode && (
                                    <p className="text-red-500 font-medium text-xs mt-1 italic">
                                      {buttonValidationErrors[index].countryCode}
                                    </p>
                                  )}
                                </div>
                                <div className="flex-1">
                                  <Label className="text-sm">Phone Number</Label>
                                  <Input
                                    className={`text-[#020617] mt-1 ${buttonValidationErrors[index]?.phoneNumber ? 'border-red-500' : ''}`}
                                    placeholder="Phone number"
                                    value={row.callPhoneNumber}
                                    onChange={(e) => updateButtonRow(index, "callPhoneNumber", e.target.value)}
                                    disabled={mode === 'view'}
                                  />
                                  {buttonValidationErrors[index]?.phoneNumber && (
                                    <p className="text-red-500 font-medium text-xs mt-1 italic">
                                      {buttonValidationErrors[index].phoneNumber}
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}

                            {row.buttonType === 'COPY_CODE' && (
                              <div>
                                <div className="flex justify-between">
                                  <Label className="text-sm">Offer Code</Label>
                                  <div className="text-xs text-gray-500 mt-0">
                                    {row.copyOfferCode.length}/15
                                  </div>
                                </div>
                                <Input
                                  className={`text-[#020617] mt-1 ${buttonValidationErrors[index]?.copyOfferCode ? 'border-red-500' : ''}`}
                                  placeholder="Offer code"
                                  value={row.copyOfferCode}
                                  onChange={(e) => updateButtonRow(index, "copyOfferCode", e.target.value)}
                                  maxLength={15}
                                  disabled={mode === 'view'}
                                />
                                {buttonValidationErrors[index]?.copyOfferCode && (
                                  <p className="text-red-500 font-medium text-xs mt-1 italic">
                                    {buttonValidationErrors[index].copyOfferCode}
                                  </p>
                                )}
                              </div>
                            )}


                            {row.buttonType === 'CUSTOM' && (
                              <div>
                                <div className="flex items-center justify-between mb-1">
                                  <Label className="text-sm">Quick Reply Responses</Label>
                                  <div className="flex items-center gap-2">
                                    <Checkbox
                                      id={`optout-${index}`}
                                      className="w-4 h-4"
                                      checked={row.optOut || false}
                                      onCheckedChange={(checked) => {
                                        const updatedRows = [...rows];
                                        updatedRows[index] = {
                                          ...updatedRows[index],
                                          optOut: checked === true,
                                          optOutType: checked ? "trip" : "trip",
                                        };
                                        setRows(updatedRows);
                                      }}
                                      disabled={mode === 'view'}
                                    />

                                    {row.optOut && (
                                      <Select
                                        value={row.optOutType || "trip"}
                                        onValueChange={(val) => {
                                          const updatedRows = [...rows];
                                          updatedRows[index] = { ...updatedRows[index], optOutType: val as "trip" | "permanent" };
                                          setRows(updatedRows);
                                        }}
                                        disabled={mode === 'view'}
                                      >
                                        <SelectTrigger className="h-7 w-[160px] text-xs">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="trip">Trip Optout</SelectItem>
                                          <SelectItem value="permanent">Permanent Optout</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    )}

                                    {!row.optOut && (
                                      <Label htmlFor={`optout-${index}`} className="text-sm cursor-pointer">
                                        OptOut
                                      </Label>
                                    )}
                                  </div>
                                </div>
                                <Textarea
                                  className="text-[#020617]"
                                  placeholder="Enter quick reply response text..."
                                  value={row.quickReplyResponse || ''}
                                  onChange={(e) => updateButtonRow(index, "quickReplyResponse", e.target.value)}
                                  rows={3}
                                  disabled={mode === 'view'}
                                />
                              </div>
                            )}

                            {mode !== 'view' && (
                              <div className="flex justify-end">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeButtonRow(index)}
                                  className="text-red-500 mt-0 w-30 hover:text-red-700 hover:bg-red-50 flex justify-end"
                                >
                                  <FaTrash className="w-3 h-4 mr-2" /> Delete Button
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {rows.length < 3 && mode !== 'view' && (
                      <Button
                        variant="outline"
                        onClick={addButtonRow}
                        className="w-full mt-2 text-[#3A85F7] border-[#3A85F7] hover:bg-blue-50"
                      >
                        <PlusIcon className="w-4 h-4 mr-2" />
                        Add Button
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>

        {/* Right: Preview */}
        <div className="w-full max-w-[500px] md:w-2/5 border rounded-lg p-4 min-h-[590px] max-h-[750px] flex flex-col">
          <h2 className="mb-2 mt-0 font-bold text-left">Content preview</h2>

          {/* SMS Preview */}
          {selectedChannelId === 2 && (
            <div className="rounded-2xl p-4 h-full bg-[#F0F4F8] overflow-y-auto flex-1">
              {noContentSelected ? (
                <div className="flex flex-col items-center justify-center h-full w-full text-center">
                  {textAreaIcon()}
                  <div className="mt-6">
                    <p className="text-xl font-semibold">Mobile screen</p>
                  </div>
                  <div className="w-[125px] mt-2" style={{ fontWeight: 500, fontSize: "14px" }} />
                </div>
              ) : (
                <div className="w-full rounded p-2 mt-4 min-h-fit">
                  <div
                    className="whitespace-pre-wrap break-words text-sm text-[#020617]"
                    dangerouslySetInnerHTML={{ __html: formatSmsText(smsBody) }}
                  />
                </div>
              )}
            </div>
          )}

          {/* WhatsApp Preview */}
          {selectedChannelId === 1 && (
            <div
              className="rounded-2xl p-4 min-h-[350px] overflow-y-auto flex-1"
              style={{
                backgroundImage: `url(${Default_WhatsApp_background})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              }}
            >
              {noContentSelected ? (
                <div className="flex w-full h-full items-center justify-center">
                  <div className="text-center">
                    {textAreaIcon()}
                    <div className="mt-6">
                      <p className="text-xl font-semibold">Mobile screen</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-lg p-3 shadow-md">
                  {headerType === "text" && textInput && (
                    <div className="font-semibold mb-2 text-lg">{textInput}</div>
                  )}

                  {headerType === "image" && (selectedFile || mediaBase64) && (
                    <div className="mb-2">
                      <img
                        src={selectedFile ? previewImage || "" : getBase64Src("image") || ""}
                        alt="Header"
                        className="w-full h-40 object-cover rounded-lg"
                      />
                    </div>
                  )}

                  {headerType === "video" && (selectedFile || mediaBase64) && (
                    <div className="mb-2">
                      {selectedFile ? (
                        <video controls className="w-full h-40 object-cover rounded-lg" src={URL.createObjectURL(selectedFile)} />
                      ) : mediaBase64 ? (
                        <video controls className="w-full h-40 object-cover rounded-lg" src={getBase64Src("video") || ""} />
                      ) : null}
                    </div>
                  )}

                  {headerType === "document" && (selectedFile || mediaBase64) && (
                    <div className="mb-2">
                      {selectedFile ? (
                        <div className="p-3 bg-gray-100 rounded-lg flex items-center space-x-2">
                          <FaFilePdf className="w-6 h-6 text-red-500" />
                          <div>
                            <div className="text-sm font-medium">{selectedFile.name}</div>
                            <div className="text-xs text-gray-500">{(selectedFile.size / 1024).toFixed(1)} KB</div>
                          </div>
                        </div>
                      ) : mediaBase64 ? (
                        <embed src={getBase64Src("document") || ""} type="application/pdf" width="100%" height="200px" className="rounded-lg" />
                      ) : null}
                    </div>
                  )}

                  {bodyText && (
                    <div className="mb-2 whitespace-pre-wrap break-words text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: renderFormattedText(bodyText) }} />
                  )}

                  {footerText && (
                    <div className="text-black font-semibold border-t pt-2 mt-3 text-[14px] flex justify-center">{footerText}</div>
                  )}

                  {rows.filter(row => row.buttonText.trim() || (row.buttonType === 'COPY_CODE' && row.copyOfferCode.trim())).length > 0 && (
                    <div className="mt-3 space-y-2">
                      {rows.filter(row => row.buttonText.trim() || (row.buttonType === 'COPY_CODE' && row.copyOfferCode.trim())).map((button, index) => (
                        <p key={index} className="font-serif text-blue-400 flex justify-center" style={{ fontSize: "14px" }}>
                          {button.buttonType === 'COPY_CODE' ? 'Copy' : button.buttonText}
                          <div className="border-t mt-2"></div>
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div >

      {mode !== 'view' && (
        <div className="flex flex-col items-end space-y-2 -mt-4 mr-0">
          <div className="flex justify-end space-x-3">
            <Dialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" className="w-[80px] border-red-500 text-red-500 hover:bg-red-500 hover:text-white mt-0" onClick={() => setShowDiscardDialog(true)}>
                  Discard
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogDescription>Are you sure you want to discard this Template?</DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button type="button" variant="outline" className="w-24">Cancel</Button>
                  </DialogClose>
                  <Button className="w-24" onClick={onClose}>OK</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Button
              className={`w-[100px] mt-0 ${isFormValid() && !isSubmitting ? "bg-[#3A85F7] text-white hover:bg-blue-600" : "bg-gray-300 text-gray-500 cursor-not-allowed"}`}
              onClick={mode === 'edit' ? handleUpdate : handleSubmit}
              disabled={!isFormValid() || isSubmitting}
            >
              {isSubmitting
                ? (mode === 'edit' ? "Updating..." : "Submitting...")
                : (mode === 'edit' ? "Update" : "Submit")
              }
            </Button>
          </div>
        </div>
      )}
    </div >
  );
};

export default CreateContentForm;